"use client";

import React, { useEffect, useRef, useState } from "react";
import { Activity, Box, Clock, Globe, MapPin, Navigation, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const nodes = [{
  id: "hub-1",
  name: "Custom RTD HQ (Ohio)",
  type: "FDA-Registered Facility",
  x: 25,
  y: 42,
  capacity: "2,000,000 cans/mo",
  leadTime: "Base Origin",
  status: "Optimal",
  isHub: true
}, {
  id: "port-1",
  name: "London Hub",
  type: "UK & EU Distribution",
  x: 48,
  y: 32,
  capacity: "High Volume",
  leadTime: "+10 Days",
  status: "Active",
  isHub: false
}, {
  id: "port-2",
  name: "Los Angeles",
  type: "West Coast Logistics",
  x: 15,
  y: 38,
  capacity: "High Volume",
  leadTime: "+3 Days",
  status: "Active",
  isHub: false
}, {
  id: "port-3",
  name: "Dubai Logistics",
  type: "MENA Regional Hub",
  x: 62,
  y: 45,
  capacity: "Medium Volume",
  leadTime: "+14 Days",
  status: "Active",
  isHub: false
}, {
  id: "port-4",
  name: "São Paulo",
  type: "LATAM Distribution",
  x: 32,
  y: 70,
  capacity: "Medium Volume",
  leadTime: "+12 Days",
  status: "Active",
  isHub: false
}, {
  id: "port-5",
  name: "Singapore Port",
  type: "APAC Distribution",
  x: 78,
  y: 65,
  capacity: "Medium Volume",
  leadTime: "+18 Days",
  status: "Active",
  isHub: false
}];
const connections = [{
  id: "c1",
  from: "hub-1",
  to: "port-1"
}, {
  id: "c2",
  from: "hub-1",
  to: "port-2"
}, {
  id: "c3",
  from: "hub-1",
  to: "port-3"
}, {
  id: "c4",
  from: "hub-1",
  to: "port-4"
}, {
  id: "c5",
  from: "hub-1",
  to: "port-5"
}];
export default function GlobalLogistics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const getNodeCoords = (id: string) => {
    const node = nodes.find(n => n.id === id);
    return node ? {
      x: node.x,
      y: node.y
    } : {
      x: 0,
      y: 0
    };
  };
  return <section data-section-id="141106" id="our-location" className="dark bg-background text-foreground relative w-full overflow-hidden py-24 lg:py-32">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-20">
        <div className="h-[800px] w-[800px] rounded-full bg-primary/10 blur-[120px]" />
      </div>
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: `linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)`,
      backgroundSize: "40px 40px"
    }} />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div ref={ref} className={`mb-16 flex flex-col items-center text-center transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-mono font-medium text-primary backdrop-blur-sm">
            <Globe className="size-4" />
            <span>GLOBAL SUPPLY CHAIN NETWORK</span>
          </div>
          <h2 className="font-sans text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl">
            Precision Canning, <br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-chart-3">
              Global Scale.
            </span>
          </h2>
          <p className="mt-6 max-w-[700px] text-muted-foreground md:text-lg">
            From our FDA-registered facility to global beverage markets, Custom RTD Co. streamlines the supply chain for functional wellness brands with rapid, compliant deployment.
          </p>
        </div>

        <div className={`relative mx-auto w-full max-w-6xl overflow-hidden rounded-2xl border border-border bg-card/50 shadow-2xl shadow-primary/5 backdrop-blur-xl transition-all duration-1000 delay-300 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="pointer-events-none absolute left-4 top-4 z-20 flex items-center gap-2 text-xs font-mono text-primary/70">
            <Activity className="size-4 animate-pulse" />
            <span>BATCH.LOGISTICS.ACTIVE</span>
          </div>
          <div className="pointer-events-none absolute bottom-4 right-4 z-20 text-xs font-mono text-muted-foreground">
            COORD: 41.6528° N, 83.5379° W
          </div>

          <div className="relative w-full pb-[60%] md:pb-[45%] lg:pb-[40%]">
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" preserveAspectRatio="none" viewBox="0 0 1000 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M150 150 Q 200 100 250 180 T 350 200 T 400 150" stroke="currentColor" strokeWidth="2" strokeDasharray="5 5" />
                <path d="M600 250 Q 650 200 700 280 T 800 300 T 850 250" stroke="currentColor" strokeWidth="2" strokeDasharray="5 5" />
                <path d="M300 350 Q 350 300 400 380 T 500 400 T 550 350" stroke="currentColor" strokeWidth="2" strokeDasharray="5 5" />
              </svg>
            </div>

            <svg className="absolute inset-0 h-full w-full pointer-events-none z-10">
              <defs>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>
              {connections.map((conn, i) => {
              const from = getNodeCoords(conn.from);
              const to = getNodeCoords(conn.to);
              return <g key={conn.id} data-index={i}>
                    <line x1={`${from.x}%`} y1={`${from.y}%`} x2={`${to.x}%`} y2={`${to.y}%`} stroke="var(--primary)" strokeWidth="1" className="opacity-30" strokeDasharray="4 4" />
                    <line x1={`${from.x}%`} y1={`${from.y}%`} x2={`${to.x}%`} y2={`${to.y}%`} stroke="var(--primary)" strokeWidth="2" filter="url(#glow)" className="opacity-60" strokeDasharray="1000" strokeDashoffset="1000">
                      <animate attributeName="stroke-dashoffset" values="1000;0" dur={`${3 + i * 0.5}s`} repeatCount="indefinite" calcMode="linear" />
                    </line>
                  </g>;
            })}
            </svg>

            <div className="absolute inset-0 z-20">
              {nodes.map((node, i) => <div key={node.id} data-index={i} className="absolute -translate-x-1/2 -translate-y-1/2" style={{
              left: `${node.x}%`,
              top: `${node.y}%`
            }}>
                  <HoverCard openDelay={100} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <Button variant="ghost" size="icon" className="group relative h-auto w-auto p-0">
                        {node.isHub && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-40"></span>}
                        <span className={`relative z-10 inline-flex rounded-full transition-all duration-300 group-hover:scale-150 ${node.isHub ? "h-4 w-4 bg-primary shadow-[0_0_15px_var(--primary)]" : "h-2.5 w-2.5 bg-primary/70 shadow-[0_0_8px_var(--primary)] group-hover:bg-primary"}`} />
                        <span className="absolute left-full ml-3 hidden whitespace-nowrap font-mono text-xs font-medium text-muted-foreground transition-colors group-hover:text-primary md:block">
                          {node.name}
                        </span>
                      </Button>
                    </HoverCardTrigger>
                    <HoverCardContent side="top" align="center" className="w-72 border-primary/30 bg-card/95 p-0 shadow-2xl shadow-primary/10 backdrop-blur-xl">
                      <div className="border-b border-primary/20 bg-primary/5 px-4 py-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-sans text-sm font-bold text-foreground">
                            {node.name}
                          </h4>
                          <span className="flex items-center gap-1 rounded-sm bg-primary/20 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary uppercase">
                            {node.status}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {node.type}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4 p-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Box className="size-3.5 text-primary" />
                            <span>Capacity</span>
                          </div>
                          <span className="font-mono text-sm font-medium text-foreground">
                            {node.capacity}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Clock className="size-3.5 text-primary" />
                            <span>Lead Time</span>
                          </div>
                          <span className="font-mono text-sm font-medium text-foreground">
                            {node.leadTime}
                          </span>
                        </div>
                      </div>
                      <div className="border-t border-primary/10 bg-muted/30 px-4 py-2">
                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                          <ShieldCheck className="size-3 text-primary" />
                          <span>Verified Custom RTD Logistics Partner</span>
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </div>)}
            </div>
          </div>
        </div>

        <div className={`mt-16 flex justify-center transition-all duration-1000 delay-500 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <Button asChild size="lg" className="group relative overflow-hidden bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/ContactPage">
              <Navigation className="mr-2 size-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              Partner with Custom RTD Co.
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}